import React, { useEffect, useRef, useState, useCallback } from 'react';
import QRCode from 'qrcode';
import { getStoredToken } from '../utils/authApi';
import { X } from 'lucide-react';
// ── Install: npm install @zxing/browser @zxing/library
// ── Then restart dev server

interface ScannedItem {
  barcode: string;
  description: string;
  qty: number;
  rate: number;
}

interface ScannedTaxInfo {
  taxRate?: number;
  b2bTaxRate?: number;
  taxInclusive?: boolean;
}

interface Props {
  onAddItem: (description: string, qty: number, rate: number, barcode?: string, taxInfo?: ScannedTaxInfo) => void;
  onClose: () => void;
  onSessionStart?: (sessionId: string) => void;
  // Tells the paired phone what it's scanning into, so its UI (and button
  // label) can say "Product Catalog" instead of always saying "POS Cart".
  context?: 'pos' | 'catalog';
}

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

const PHONE_SCAN_URL = `${import.meta.env.VITE_APP_URL || 'https://cloudairambo.com'}/phone-scan`;

type Tab = 'camera' | 'phone' | 'manual' | 'ocr';

// Shared parser for label photos — same regex approach as the OCR tab,
// reused here so the camera-tab capture behaves the same way.
// Same "PRD-<timestamp>" shape as ProductCatalogView's generateBarcode(),
// for when the product genuinely has no barcode to scan.
function generateFallbackBarcode() {
  return `PRD-${Date.now().toString().slice(-7)}`;
}

function parseLabelLines(lines: string[]) {
  let name = '';
  let price = '';
  let qty = '1';
  for (const line of lines) {
    const priceMatch = line.match(/(?:₹|Rs\.?|MRP|Price|rate|amt)[:\s]*([0-9]+(?:\.[0-9]{1,2})?)/i);
    if (priceMatch && !price) { price = priceMatch[1]; continue; }
    const qtyMatch = line.match(/(?:qty|pcs|nos|pack)[:\s]*([0-9]+)/i);
    if (qtyMatch && qty === '1') { qty = qtyMatch[1]; continue; }
    if (!name && line.length > 2 && !/^[0-9\s\.\,\-]+$/.test(line) &&
        !/(?:barcode|batch|exp|mfg|date|gstin|tel|ph:|www\.|@)/i.test(line)) {
      name = line;
    }
  }
  return { name: name || lines[0] || '', price, qty };
}

// ── Colour tokens (matches CloudAI Rambo gradient: #3b82f6 → #9333ea)
const C = {
  grad: 'linear-gradient(135deg, #3b82f6, #9333ea)',
  gradLight: 'linear-gradient(135deg, #eff6ff, #f5f3ff)',
  blue: '#3b82f6',
  purple: '#9333ea',
  green: '#10b981',
  red: '#ef4444',
  bg: '#0f172a',          // scanner dark bg
  surface: '#1e293b',
  border: '#334155',
  textMuted: '#94a3b8',
};

export default function QuickScannerModal({ onAddItem, onClose, onSessionStart, context = 'pos' }: Props) {
  const [tab, setTab] = useState<Tab>('camera');
  const [minimized, setMinimized] = useState(false);
  const [showAppBanner, setShowAppBanner] = useState(
    () => localStorage.getItem('cloudairambo_dismiss_app_banner') !== '1'
  );
  const dismissAppBanner = () => {
    localStorage.setItem('cloudairambo_dismiss_app_banner', '1');
    setShowAppBanner(false);
  };

  // ── shared state ──────────────────────────────────────────────────────────
  const [sessionItems, setSessionItems] = useState<ScannedItem[]>([]);
  const [lastAdded, setLastAdded] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── camera tab ────────────────────────────────────────────────────────────
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<any>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [lastScan, setLastScan] = useState<string | null>(null);
  const lastScanRef = useRef<string | null>(null);
  const cooldownRef = useRef(false);
  const scanLinePos = useRef(0);
  const animFrameRef = useRef<number>(0);
  const canvasOverlayRef = useRef<HTMLCanvasElement>(null);

  // ── Unknown-barcode label capture, right on the camera tab — no phone
  // needed. Triggers when a scanned barcode doesn't match a product. ────────
  const [labelBarcode, setLabelBarcode] = useState<string | null>(null);
  const [labelPhase, setLabelPhase] = useState<'name' | 'price' | 'review' | null>(null);
  const [labelParsed, setLabelParsed] = useState<{ name: string; price: string; qty: string } | null>(null);
  const [labelCapturing, setLabelCapturing] = useState(false);
  const labelCanvasRef = useRef<HTMLCanvasElement>(null);

  // ── phone tab ─────────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState<string | null>(null);
  useEffect(() => {
    const token = getStoredToken();
    fetch(`${BASE}/scan-session/create`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        if (data.session_id) {
          setSessionId(data.session_id);
          onSessionStart?.(data.session_id);
        } else {
          setError('Could not start scan session — please sign in again.');
        }
      })
      .catch(() => setError('Could not start scan session — check your connection.'));
  }, []);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [phoneConnected, setPhoneConnected] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // ── OCR tab ───────────────────────────────────────────────────────────────
  const [ocrImage, setOcrImage] = useState<string | null>(null);
  const [ocrText, setOcrText] = useState('');
  const [ocrParsed, setOcrParsed] = useState<{name: string; price: string; qty: string} | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const ocrInputRef = useRef<HTMLInputElement>(null);
  const ocrVideoRef = useRef<HTMLVideoElement>(null);
  const ocrCanvasRef = useRef<HTMLCanvasElement>(null);
  const [ocrCameraActive, setOcrCameraActive] = useState(false);
  const pendingOcrStream = useRef<MediaStream | null>(null);

  // Attach the pending camera stream once the <video> element actually mounts
  // (it only renders after ocrCameraActive flips true, so the stream can't be
  // attached synchronously inside startOcrCamera when it first requests it).
  useEffect(() => {
    if (ocrCameraActive && pendingOcrStream.current && ocrVideoRef.current) {
      ocrVideoRef.current.srcObject = pendingOcrStream.current;
      ocrVideoRef.current.play().catch(() => {});
      pendingOcrStream.current = null;
    }
  }, [ocrCameraActive]);
  // ── manual tab ────────────────────────────────────────────────────────────
  const [manualCode, setManualCode] = useState('');
  const manualRef = useRef<HTMLInputElement>(null);
  const usbBuffer = useRef('');
  const usbTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── ESC close ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // ── USB gun (global keydown) ───────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (document.activeElement === manualRef.current) return;
      if (e.key === 'Enter') {
        const code = usbBuffer.current.trim();
        usbBuffer.current = '';
        if (code.length > 2) lookupAndAdd(code);
      } else if (e.key.length === 1) {
        usbBuffer.current += e.key;
        if (usbTimer.current) clearTimeout(usbTimer.current);
        usbTimer.current = setTimeout(() => { usbBuffer.current = ''; }, 80);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // ── Product lookup ─────────────────────────────────────────────────────────
  const lookupAndAdd = useCallback(async (code: string) => {
    if (!code) return;
    setError(null);
    setLoading(true);
    try {
      let description = '';
      let rate = 0;
      let found = false;
      // Carried straight from the catalog lookup — lets whatever's on the
      // other end of onAddItem (QuickInvoice, POS) know this item has a
      // B2B tax override, instead of only ever getting the B2C rate.
      let scannedTaxInfo: ScannedTaxInfo | undefined;
      try {
        const token = getStoredToken();
        const res = await fetch(`${BASE}/products/lookup?barcode=${encodeURIComponent(code)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.found && data.product) {
            description = data.product.name;
            rate = data.product.rate || 0;
            found = true;
            scannedTaxInfo = {
              taxRate: data.product.tax_rate,
              b2bTaxRate: data.product.b2b_tax_rate,
              taxInclusive: data.product.tax_inclusive,
            };
          }
        }
      } catch { /* fallback below */ }

      // Known barcode → add straight away, same as before.
      if (found) {
        setSessionItems(prev => {
          const existing = prev.findIndex(i => i.barcode === code);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = { ...updated[existing], qty: updated[existing].qty + 1 };
            onAddItem(description, 1, rate, code, scannedTaxInfo);
            return updated;
          }
          onAddItem(description, 1, rate, code, scannedTaxInfo);
          return [...prev, { barcode: code, description, qty: 1, rate }];
        });
        setLastAdded(description);
        setTimeout(() => setLastAdded(null), 2000);
        setLoading(false);
        return;
      }

      // Unknown barcode, scanned via the live camera — snap the label
      // instead of adding a blank "Item (code)" / ₹0 placeholder.
      if (tab === 'camera') {
        scanLoopActive.current = false; // pause barcode detection while capturing
        setLabelBarcode(code);
        setLabelPhase('name');
        setLoading(false);
        return;
      }

      // Non-camera source (USB gun / manual / phone barcode) — keep old
      // placeholder behaviour, since there's no live feed to capture from.
      description = `Item (${code})`;
      setSessionItems(prev => {
        const existing = prev.findIndex(i => i.barcode === code);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { ...updated[existing], qty: updated[existing].qty + 1 };
          onAddItem(description, 1, rate, code);
          return updated;
        }
        onAddItem(description, 1, rate, code);
        return [...prev, { barcode: code, description, qty: 1, rate }];
      });
      setLastAdded(description);
      setTimeout(() => setLastAdded(null), 2000);
    } catch {
      setError(`Lookup failed: ${code}`);
    } finally {
      setLoading(false);
    }
  }, [onAddItem, tab]);

 // ── Camera: start ZXing ───────────────────────────────────────────────────
const startCamera = useCallback(async () => {
  setCameraError(null);
  try {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
    });

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraActive(true);
      startScanLoop(codeReader);
      startScanLineAnimation();
    }
  } catch (err: any) {
    if (err?.name === 'NotAllowedError') {
      setCameraError('Camera permission denied. Allow camera access and try again.');
    } else {
      setCameraError('Camera unavailable: ' + (err?.message || 'Unknown error'));
    }
  }
}, []);

const scanLoopActive = useRef(false);

const startScanLoop = useCallback((codeReader: any) => {
  if (!videoRef.current) return;
  scanLoopActive.current = true;

  const loop = async () => {
    while (scanLoopActive.current && videoRef.current) {
      try {
        const result = await codeReader.decodeOnceFromVideoElement(videoRef.current);
        if (result) {
          const text = result.getText();
          if (text && !cooldownRef.current && text !== lastScanRef.current) {
            lastScanRef.current = text;
            cooldownRef.current = true;
            setLastScan(text);
            flashOverlay();
            lookupAndAdd(text);
            setTimeout(() => {
              cooldownRef.current = false;
              lastScanRef.current = null;
              setLastScan(null);
            }, 800);
          }
        }
      } catch {
        // No barcode found this frame — keep looping
        await new Promise(r => setTimeout(r, 100));
      }
    }
  };

  loop();
}, [lookupAndAdd]);

  // Snaps the currently-playing video frame (no new getUserMedia call —
  // reuses the scanning stream already open) and OCRs it for name/price.
  const captureLabelFrame = async () => {
    const video = videoRef.current;
    const canvas = labelCanvasRef.current;
    if (!video || !canvas || !labelPhase || labelPhase === 'review') return;
    setLabelCapturing(true);
    setError(null);
    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

      const Tesseract = await import('tesseract.js');
      const result = await Tesseract.recognize(dataUrl, 'eng', { logger: () => {} });
      const lines = result.data.text.trim().split('\n').map((l: string) => l.trim()).filter(Boolean);
      const { name, price, qty } = parseLabelLines(lines);

      if (labelPhase === 'name') {
        if (!name) { setError('No name found in that photo — try again.'); return; }
        setLabelParsed(prev => ({ name, price: prev?.price || '', qty: prev?.qty || '1' }));
        setLabelPhase('price');
      } else {
        if (!price) { setError('No price found — try again or type it manually.'); return; }
        setLabelParsed(prev => ({ name: prev?.name || '', price, qty: qty !== '1' ? qty : (prev?.qty || '1') }));
        setLabelPhase('review');
      }
    } catch {
      setError('Could not read that photo — try again with better lighting.');
    } finally {
      setLabelCapturing(false);
    }
  };

  const confirmLabelAdd = () => {
    if (!labelParsed?.name.trim() || !labelBarcode) return;
    const name = labelParsed.name.trim();
    const rate = parseFloat(labelParsed.price) || 0;
    const qty = parseInt(labelParsed.qty) || 1;
    onAddItem(name, qty, rate, labelBarcode);
    setSessionItems(prev => [...prev, { barcode: labelBarcode, description: name, qty, rate }]);
    setLastAdded(name);
    setTimeout(() => setLastAdded(null), 2000);
    cancelLabelCapture();
  };

  const skipBarcodeCapture = () => {
    scanLoopActive.current = false; // pause barcode detection while capturing
    setLabelBarcode(generateFallbackBarcode());
    setLabelPhase('name');
  };

  const cancelLabelCapture = () => {
    setLabelBarcode(null);
    setLabelPhase(null);
    setLabelParsed(null);
    lastScanRef.current = null;
    if (tab === 'camera' && codeReaderRef.current) startScanLoop(codeReaderRef.current); // resume scanning
  };

  const stopCamera = useCallback(() => {
  scanLoopActive.current = false;
  const video = videoRef.current;
  if (video?.srcObject) {
      const stream = video.srcObject as MediaStream;
      (stream as any).__stopLoop?.();
      stream.getTracks().forEach(t => t.stop());
      video.srcObject = null;
    }
    cancelAnimationFrame(animFrameRef.current);
    setCameraActive(false);
    setLastScan(null);
  }, []);

  // Scan-line canvas animation
  const startScanLineAnimation = () => {
    const canvas = canvasOverlayRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let pos = 0;
    let dir = 1;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Corner brackets
      const bSize = 28, bThick = 3, pad = 20;
      const w = canvas.width, h = canvas.height;
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = bThick;
      ctx.lineCap = 'round';
      const corners = [
        [pad, pad, 1, 1], [w - pad, pad, -1, 1],
        [pad, h - pad, 1, -1], [w - pad, h - pad, -1, -1],
      ];
      corners.forEach(([x, y, dx, dy]) => {
        ctx.beginPath(); ctx.moveTo(x, y + dy * bSize); ctx.lineTo(x, y); ctx.lineTo(x + dx * bSize, y); ctx.stroke();
      });

      // Scan line gradient
      pos += dir * 1.2;
      if (pos > h - pad * 2) dir = -1;
      if (pos < 0) dir = 1;
      const grad = ctx.createLinearGradient(0, pad + pos - 2, 0, pad + pos + 2);
      grad.addColorStop(0, 'transparent');
      grad.addColorStop(0.5, '#3b82f680');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fillRect(pad, pad + pos - 1, w - pad * 2, 3);

      // Bright center line
      const lineGrad = ctx.createLinearGradient(pad, 0, w - pad, 0);
      lineGrad.addColorStop(0, 'transparent');
      lineGrad.addColorStop(0.2, '#3b82f6');
      lineGrad.addColorStop(0.5, '#a78bfa');
      lineGrad.addColorStop(0.8, '#3b82f6');
      lineGrad.addColorStop(1, 'transparent');
      ctx.strokeStyle = lineGrad;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(pad, pad + pos);
      ctx.lineTo(w - pad, pad + pos);
      ctx.stroke();

      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  const flashOverlay = () => {
    const canvas = canvasOverlayRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = 'rgba(59,130,246,0.25)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setTimeout(() => ctx.clearRect(0, 0, canvas.width, canvas.height), 150);
  };

  // Start camera when tab = camera
 useEffect(() => {
  if (tab === 'camera') {
    startCamera();
  } else {
    stopCamera();
  }
  if (tab !== 'ocr') {
    stopOcrCamera();
  }
  return () => {
    if (tab === 'camera') stopCamera();
    if (tab === 'ocr') stopOcrCamera();
  };
}, [tab]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── Phone QR ──────────────────────────────────────────────────────────────
  const phoneUrl = sessionId ? `${PHONE_SCAN_URL}?session=${sessionId}&ctx=${context}` : '';
  useEffect(() => {
    if (!sessionId) return;
    QRCode.toDataURL(phoneUrl, { width: 200, margin: 1, color: { dark: '#111827', light: '#ffffff' } })
      .then(setQrDataUrl).catch(() => {});
  }, [phoneUrl, sessionId]);

// ── Poll phone session ────────────────────────────────────────────────────
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!sessionId) return;
    pollRef.current = setInterval(async () => {
      try {
        const token = getStoredToken();
        const res = await fetch(`${BASE}/scan-session/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!data || Object.keys(data).length === 0) return;
        setPhoneConnected(true);
        if (data.type === 'connected') {
          setMinimized(true);
          return;
        }
        if (data.type === 'label') {
          const name = data.name?.trim() || '';
          const qty = parseInt(data.qty) || 1;
          const rate = parseFloat(data.price) || 0;
          if (!name) return;
          onAddItem(name, qty, rate);
          setSessionItems(prev => {
            const existing = prev.findIndex(i => i.description === name);
            if (existing >= 0) {
              const u = [...prev];
              u[existing].qty += qty;
              return u;
            }
            return [...prev, { barcode: 'LABEL', description: name, qty, rate }];
          });
          setLastAdded(name);
          setTimeout(() => setLastAdded(null), 2000);
        } else if (typeof data.value === 'string' && data.value.startsWith('OCR_ITEM:')) {
          try {
            const parsed = JSON.parse(data.value.slice('OCR_ITEM:'.length));
            const name = parsed.name?.trim();
            if (!name) return;
            onAddItem(name, parsed.qty || 1, parsed.price || 0);
            setSessionItems(prev => {
              const existing = prev.findIndex(i => i.description === name);
              if (existing >= 0) {
                const u = [...prev];
                u[existing].qty += parsed.qty || 1;
                return u;
              }
              return [...prev, { barcode: 'OCR', description: name, qty: parsed.qty || 1, rate: parsed.price || 0 }];
            });
            setLastAdded(name);
            setTimeout(() => setLastAdded(null), 2000);
          } catch { /* malformed payload — ignore */ }
        } else if (data.type === 'barcode' && data.value) {
          lookupAndAdd(data.value);
        } else if (data.value) {
          // legacy shape
          lookupAndAdd(data.value);
        }
      } catch {}
    }, 1000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId, lookupAndAdd]);

  // ── Particle background (canvas) ─────────────────────────────────────────
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number; color: string }[] = [];
    const colors = ['#3b82f6', '#9333ea', '#06b6d4', '#8b5cf6'];
    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.5 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let bgFrame: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
      });
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 80) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(99,102,241,${0.12 * (1 - dist / 80)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      bgFrame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(bgFrame);
  }, []);

  const runOCR = async (imageData: string) => {
  setOcrLoading(true);
  setOcrText('');
  setOcrParsed(null);
  try {
    const Tesseract = await import('tesseract.js');
    const result = await Tesseract.recognize(imageData, 'eng', {
      logger: () => {},
    });
    const text = result.data.text.trim();
    setOcrText(text);

    const lines = text.split('\n').map((l: string) => l.trim()).filter(Boolean);
    let name = '';
    let price = '';
    let qty = '1';

    for (const line of lines) {
      const priceMatch = line.match(/(?:₹|Rs\.?|MRP|Price|rate|amt)[:\s]*([0-9]+(?:\.[0-9]{1,2})?)/i);
      if (priceMatch && !price) { price = priceMatch[1]; continue; }

      const qtyMatch = line.match(/(?:qty|pcs|nos|pack)[:\s]*([0-9]+)/i);
      if (qtyMatch && qty === '1') { qty = qtyMatch[1]; continue; }

      if (!name && line.length > 2 && !/^[0-9\s\.\,\-]+$/.test(line) &&
          !/(?:barcode|batch|exp|mfg|date|gstin|tel|ph:|www\.|@)/i.test(line)) {
        name = line;
      }
    }

    setOcrParsed({ name: name || lines[0] || '', price, qty });
  } catch (err) {
    setOcrText('OCR failed — try a clearer image');
  } finally {
    setOcrLoading(false);
  }
};

const startOcrCamera = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
    });
    pendingOcrStream.current = stream;
    setOcrCameraActive(true); // mounts <video>; effect above attaches the stream
  } catch (err: any) {
    setOcrCameraActive(false);
    setError(err?.name === 'NotAllowedError'
      ? 'Camera permission denied. Allow camera access and try again.'
      : 'Camera unavailable: ' + (err?.message || 'Unknown error'));
  }
};

const stopOcrCamera = () => {
  const video = ocrVideoRef.current;
  if (video?.srcObject) {
    (video.srcObject as MediaStream).getTracks().forEach(t => t.stop());
    video.srcObject = null;
  }
  setOcrCameraActive(false);
};

const captureOcrFrame = () => {
  const video = ocrVideoRef.current;
  const canvas = ocrCanvasRef.current;
  if (!video || !canvas) return;
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  setOcrImage(dataUrl);
  stopOcrCamera();
  runOCR(dataUrl);
};


  const totalItems = sessionItems.reduce((s, i) => s + i.qty, 0);

  // ── Tab button ─────────────────────────────────────────────────────────────
  const TabBtn = ({ id, icon, label }: { id: Tab; icon: string; label: string }) => (
    <button
      onClick={() => setTab(id)}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 5, padding: '8px 6px', border: 'none', cursor: 'pointer',
        fontSize: 11, fontWeight: 700, borderRadius: 8, transition: 'all 0.2s',
        background: tab === id ? C.grad : 'transparent',
        color: tab === id ? '#fff' : C.textMuted,
        boxShadow: tab === id ? '0 2px 10px rgba(99,102,241,0.4)' : 'none',
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span> {label}
    </button>
  );

  if (minimized) return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 300,
      background: 'linear-gradient(135deg, #10b981, #059669)',
      borderRadius: 40, padding: '10px 20px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 8px 32px rgba(16,185,129,0.4)',
      cursor: 'pointer', userSelect: 'none',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }} onClick={() => setMinimized(false)}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'pulse 1.5s ease-in-out infinite' }} />
      <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>📱 Phone scanning — {totalItems} items</span>
      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>tap to expand</span>
    </div>
  );

 return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(5,10,20,0.82)', backdropFilter: 'blur(8px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 620, maxWidth: '96vw', maxHeight: '94vh',
          display: 'flex', flexDirection: 'column',
          borderRadius: 20, overflow: 'hidden',
          background: C.bg,
          border: '1px solid ' + C.border,
          boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.15)',
          position: 'relative',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        {/* Animated particle background */}
        <canvas
          ref={bgCanvasRef}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            pointerEvents: 'none', zIndex: 0,
          }}
        />

        {/* Content above particles */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{
            padding: '16px 20px 12px',
            background: 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.7) 100%)',
            borderBottom: '1px solid ' + C.border,
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Logo */}
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: C.grad,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, boxShadow: '0 0 16px rgba(99,102,241,0.5)',
                }}>🔍</div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: '-0.01em' }}>
                    Barcode Scanner
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 500 }}>
                    CloudAI Rambo — {context === 'catalog' ? 'Product Catalog' : 'POS'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {totalItems > 0 && (
                  <div style={{
                    background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)',
                    borderRadius: 20, padding: '3px 10px',
                    fontSize: 11, fontWeight: 700, color: '#a5b4fc',
                  }}>
                    {totalItems} {context === 'catalog' ? 'product' : 'item'}{totalItems !== 1 ? 's' : ''} added
                  </div>
                )}
                <button
                  onClick={() => phoneConnected ? setMinimized(true) : onClose()}
                  style={{
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8, padding: '6px 14px', color: '#e2e8f0',
                    fontWeight: 700, fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {phoneConnected ? 'Minimize ↙' : 'Done ✕'}
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex', gap: 4,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 10, padding: 4,
            }}>
              <TabBtn id="camera" icon="📷" label="Camera" />
              <TabBtn id="phone"  icon="📱" label="Phone QR" />
              <TabBtn id="manual" icon="⌨️" label="Manual / USB" />
              <TabBtn id="ocr"    icon="🏷️" label="Read Label" />
            </div>
          </div>

          {/* ── App recommendation banner ── */}
          {showAppBanner && (
            <div style={{
              margin: '10px 16px 0', flexShrink: 0,
              background: 'linear-gradient(135deg, rgba(59,130,246,0.14), rgba(147,51,234,0.14))',
              border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 12, padding: '9px 12px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>📲</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#e2e8f0' }}>
                  Scanning works best in the CloudAI Rambo app
                </div>
                <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 1 }}>
                  Faster barcode detection and a steadier camera than the browser.
                </div>
              </div>
              
              <a  href="https://play.google.com/store/apps/details?id=com.cloudairambo.app"
                target="_blank" rel="noreferrer"
                style={{
                  flexShrink: 0, textDecoration: 'none',
                  background: C.grad, color: '#fff',
                  fontSize: 11, fontWeight: 700,
                  padding: '6px 12px', borderRadius: 8,
                  whiteSpace: 'nowrap',
                }}
              >
                Get App
              </a>
              <button onClick={dismissAppBanner}
                style={{
                  flexShrink: 0, background: 'none', border: 'none',
                  color: C.textMuted, cursor: 'pointer', padding: 2,
                }}>
                <X size={14} />
              </button>
            </div>
          )}

          {/* ── Tab body ── */}
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

            {/* ════ CAMERA TAB ════ */}
            {tab === 'camera' && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Video viewport */}
                <div style={{
                  position: 'relative', background: '#000',
                  aspectRatio: '4/3.2', width: '100%', overflow: 'hidden',
                  flexShrink: 0, minHeight: 380,
                }}>
                  <video
                    ref={videoRef}
                    muted
                    playsInline
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      display: cameraActive ? 'block' : 'none',
                    }}
                  />

                  {/* Overlay canvas (scan line + brackets) */}
                  <canvas
                    ref={canvasOverlayRef}
                    width={620} height={484}
                    style={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%',
                      pointerEvents: 'none',
                      display: cameraActive ? 'block' : 'none',
                    }}
                  />

                  {/* Idle / error state */}
                  {!cameraActive && (
                    <div style={{
                      position: 'absolute', inset: 0,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(15,23,42,0.95)',
                      gap: 12,
                    }}>
                      {cameraError ? (
                        <>
                          <div style={{ fontSize: 36 }}>⚠️</div>
                          <div style={{ color: '#fca5a5', fontSize: 12, fontWeight: 600, textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}>
                            {cameraError}
                          </div>
                          {cameraError.includes('npm install') && (
                            <div style={{
                              background: 'rgba(0,0,0,0.4)', border: '1px solid ' + C.border,
                              borderRadius: 8, padding: '8px 14px',
                              fontFamily: 'monospace', fontSize: 11, color: '#a5b4fc',
                            }}>
                              npm install @zxing/browser @zxing/library
                            </div>
                          )}
                          <button
                            onClick={startCamera}
                            style={{
                              background: C.grad, border: 'none', borderRadius: 10,
                              padding: '8px 20px', color: '#fff', fontWeight: 700,
                              fontSize: 12, cursor: 'pointer',
                              boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
                            }}
                          >
                            Try Again
                          </button>
                        </>
                      ) : (
                        <>
                          <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(99,102,241,0.15)',
                            border: '2px solid rgba(99,102,241,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 26,
                          }}>📷</div>
                          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 600 }}>
                            Starting camera...
                          </div>
                          <div style={{
                            width: 32, height: 32,
                            border: '3px solid rgba(99,102,241,0.2)',
                            borderTopColor: C.blue,
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                          }} />
                        </>
                      )}
                    </div>
                  )}

                  {/* Last scan flash */}
                  {lastScan && (
                    <div style={{
                      position: 'absolute', bottom: 10, left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(16,185,129,0.92)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: 10, padding: '6px 16px',
                      fontSize: 12, fontWeight: 700, color: '#fff',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 4px 16px rgba(16,185,129,0.5)',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}>
                      ✅ Scanned: {lastScan}
                    </div>
                  )}

                  {/* Active indicator */}
                  {cameraActive && !lastScan && !labelPhase && (
                    <div style={{
                      position: 'absolute', top: 10, left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(15,23,42,0.7)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: 20, padding: '4px 12px',
                      fontSize: 10, fontWeight: 700, color: '#a5b4fc',
                      border: '1px solid rgba(99,102,241,0.3)',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <div style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: C.green,
                        boxShadow: `0 0 6px ${C.green}`,
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }} />
                      Scanning for barcodes...
                    </div>
                  )}

                  {/* No barcode on this product — skip straight to name/price capture.
                      Catalog-only: POS items are transient cart lines, so there's no
                      reason to mint a permanent-looking PRD- code for them. */}
                  {context === 'catalog' && cameraActive && !lastScan && !labelPhase && (
                    <button onClick={skipBarcodeCapture}
                      style={{
                        position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                        background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20,
                        padding: '6px 14px', fontSize: 11, fontWeight: 700, color: '#e2e8f0',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                      No barcode on this product? →
                    </button>
                  )}

                  {/* Unknown-barcode label capture overlay — bottom sheet only,
                      so the live camera stays visible above it for aiming. */}
                  {labelPhase && (
                    <div style={{
                      position: 'absolute', left: 0, right: 0, bottom: 0,
                      background: 'linear-gradient(180deg, transparent, rgba(15,23,42,0.55) 25%, rgba(15,23,42,0.96) 55%)',
                      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                      padding: 16, paddingTop: 60,
                    }}>
                      <div style={{
                        width: '100%', background: 'rgba(30,41,59,0.97)',
                        border: '1px solid ' + C.border, borderRadius: 14,
                        padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.textMuted }}>
                          <span>Barcode: <span style={{ color: '#fff', fontWeight: 700 }}>{labelBarcode}</span></span>
                          <span style={{ fontWeight: 700 }}>
                            {labelPhase === 'review' ? 'Review' : labelPhase === 'name' ? 'Step 1 of 2' : 'Step 2 of 2'}
                          </span>
                        </div>

                        {labelPhase !== 'review' ? (
                          <>
                            <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, textAlign: 'center' }}>
                              New barcode — aim at the {labelPhase === 'name' ? 'product name' : 'price / MRP'} and capture
                            </div>
                            {labelParsed?.name && labelPhase === 'price' && (
                              <div style={{ fontSize: 11, color: '#a5b4fc' }}>Name: {labelParsed.name}</div>
                            )}
                            <button onClick={captureLabelFrame} disabled={labelCapturing}
                              style={{
                                background: C.grad, border: 'none', borderRadius: 10,
                                padding: '10px', color: '#fff', fontWeight: 800, fontSize: 13,
                                cursor: labelCapturing ? 'default' : 'pointer', opacity: labelCapturing ? 0.7 : 1,
                              }}>
                              {labelCapturing ? 'Reading…' : `📸 Capture ${labelPhase === 'name' ? 'Name' : 'Price'}`}
                            </button>
                            {labelPhase === 'price' && (
                              <button onClick={() => setLabelPhase('review')}
                                style={{ background: 'none', border: 'none', color: C.textMuted, fontSize: 11, cursor: 'pointer' }}>
                                Skip — I'll type the price
                              </button>
                            )}
                            <button onClick={cancelLabelCapture}
                              style={{ background: 'none', border: 'none', color: '#fca5a5', fontSize: 11, cursor: 'pointer' }}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <input value={labelParsed?.name || ''} onChange={e => setLabelParsed(p => p ? { ...p, name: e.target.value } : p)}
                              placeholder="Product name"
                              style={{ padding: '8px 10px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: '#e2e8f0', outline: 'none' }} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <input value={labelParsed?.price || ''} onChange={e => setLabelParsed(p => p ? { ...p, price: e.target.value } : p)}
                                type="number" placeholder="Price"
                                style={{ padding: '8px 10px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: '#67e8f9', fontWeight: 700, outline: 'none' }} />
                              <input value={labelParsed?.qty || '1'} onChange={e => setLabelParsed(p => p ? { ...p, qty: e.target.value } : p)}
                                type="number" min="1" placeholder="Qty"
                                style={{ padding: '8px 10px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: '#e2e8f0', outline: 'none' }} />
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => { setLabelPhase('name'); setLabelParsed(null); }}
                                style={{ flex: 1, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '9px', color: '#e2e8f0', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                ↩ Retry
                              </button>
                              <button onClick={confirmLabelAdd}
                                style={{ flex: 2, background: C.grad, border: 'none', borderRadius: 8, padding: '9px', color: '#fff', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
                                ➕ Add {context === 'catalog' ? 'to Catalog' : 'to Cart'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <canvas ref={labelCanvasRef} style={{ display: 'none' }} />
                </div>

                {/* Scan hint */}
                <div style={{
                  padding: '10px 16px', flexShrink: 0,
                  background: 'rgba(30,41,59,0.8)',
                  borderBottom: '1px solid ' + C.border,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <div style={{ fontSize: 18 }}>💡</div>
                  <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.5 }}>
                    <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Point camera at any barcode.</span>{' '}
                    Supports QR, Code128, EAN-13, EAN-8, UPC-A, Code39, DataMatrix and more.
                    {context === 'catalog'
                      ? ' Existing barcodes bump stock — new ones create a product.'
                      : ' Keep barcode centered in frame for best results.'}
                  </div>
                </div>
              </div>
            )}

            {/* ════ PHONE TAB ════ */}
            {tab === 'phone' && (
              <div style={{ padding: '16px 18px' }}>
                <div style={{
                  background: 'rgba(30,41,59,0.8)',
                  border: '1px solid ' + C.border,
                  borderRadius: 14, padding: '16px',
                  display: 'flex', gap: 16, alignItems: 'flex-start',
                }}>
                  {qrDataUrl ? (
                    <div style={{
                      borderRadius: 10, overflow: 'hidden',
                      border: `2px solid ${phoneConnected ? C.green : C.blue}`,
                      boxShadow: `0 0 16px ${phoneConnected ? 'rgba(16,185,129,0.4)' : 'rgba(59,130,246,0.3)'}`,
                      flexShrink: 0,
                    }}>
                      <img src={qrDataUrl} style={{ width: 100, height: 100, display: 'block' }} />
                    </div>
                  ) : (
                    <div style={{ width: 100, height: 100, background: C.surface, borderRadius: 10, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: phoneConnected ? C.green : '#f59e0b',
                        boxShadow: `0 0 6px ${phoneConnected ? C.green : '#f59e0b'}`,
                      }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: phoneConnected ? '#34d399' : '#fbbf24' }}>
                        {phoneConnected ? 'Phone Connected ✓' : 'Waiting for phone...'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.7 }}>
                      1. Open phone camera<br />
                      2. Scan the QR code<br />
                      3. Allow camera on phone<br />
                      4. {context === 'catalog' ? 'Scan products to add them' : 'Scan product barcodes'}
                    </div>
                    <div style={{
                      marginTop: 8, fontSize: 9, fontFamily: 'monospace',
                      color: C.textMuted, background: 'rgba(0,0,0,0.3)',
                      padding: '4px 8px', borderRadius: 6, wordBreak: 'break-all',
                      border: '1px solid ' + C.border,
                    }}>
                      {phoneUrl}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ════ MANUAL TAB ════ */}
            {tab === 'manual' && (
              <div style={{ padding: '16px 18px' }}>
                <div style={{
                  background: 'rgba(30,41,59,0.8)',
                  border: '1px solid ' + C.border,
                  borderRadius: 14, padding: '16px',
                }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: C.textMuted,
                    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10,
                  }}>
                    Type barcode or use USB scanner gun
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      ref={manualRef}
                      autoFocus={tab === 'manual'}
                      style={{
                        flex: 1, padding: '9px 12px',
                        background: C.surface, border: '1px solid ' + C.border,
                        borderRadius: 10, fontSize: 13, fontFamily: 'monospace',
                        fontWeight: 600, outline: 'none', color: '#e2e8f0',
                      }}
                      placeholder="Barcode / USB gun scan..."
                      value={manualCode}
                      onChange={e => setManualCode(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          const code = manualCode.trim();
                          if (!code) return;
                          setManualCode('');
                          lookupAndAdd(code);
                          manualRef.current?.focus();
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const code = manualCode.trim();
                        if (!code) return;
                        setManualCode('');
                        lookupAndAdd(code);
                        manualRef.current?.focus();
                      }}
                      style={{
                        background: C.grad, border: 'none', borderRadius: 10,
                        padding: '9px 18px', color: '#fff', fontWeight: 700,
                        fontSize: 12, cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(99,102,241,0.4)',
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 8 }}>
                    💡 USB scanner guns work automatically — just scan anywhere on this screen
                  </div>
                  {error && (
                    <div style={{
                      marginTop: 8, color: '#fca5a5', fontSize: 11,
                      background: 'rgba(239,68,68,0.1)', borderRadius: 6, padding: '5px 8px',
                    }}>
                      {error}
                    </div>
                  )}
                </div>
              </div>
            )}

           {/* ════ OCR / LABEL TAB ════ */}
{tab === 'ocr' && (
  <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>

    {ocrCameraActive ? (
      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
        <video ref={ocrVideoRef} muted playsInline style={{ width: '100%', borderRadius: 12 }} />
        <canvas ref={ocrCanvasRef} style={{ display: 'none' }} />
        <button onClick={captureOcrFrame} style={{
          position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg,#3b82f6,#9333ea)', border: 'none',
          borderRadius: 30, padding: '10px 28px', color: '#fff',
          fontWeight: 800, fontSize: 13, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(99,102,241,0.5)',
        }}>
          📸 Capture Label
        </button>
        <button onClick={stopOcrCamera} style={{
          position: 'absolute', top: 10, right: 10,
          background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 8,
          padding: '4px 10px', color: '#fff', fontSize: 11, cursor: 'pointer',
        }}>✕</button>
      </div>
    ) : (
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => { setOcrImage(null); setOcrText(''); setOcrParsed(null); startOcrCamera(); }}
          style={{
            flex: 1, padding: '12px', borderRadius: 12, border: '2px dashed rgba(99,102,241,0.4)',
            background: 'rgba(99,102,241,0.08)', color: '#a5b4fc',
            fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          📷 Take Photo of Label
        </button>
        <button onClick={() => ocrInputRef.current?.click()}
          style={{
            flex: 1, padding: '12px', borderRadius: 12, border: '2px dashed rgba(99,102,241,0.4)',
            background: 'rgba(99,102,241,0.08)', color: '#a5b4fc',
            fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          🖼 Upload Label Image
        </button>
        <input ref={ocrInputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = ev => {
              const dataUrl = ev.target?.result as string;
              setOcrImage(dataUrl);
              runOCR(dataUrl);
            };
            reader.readAsDataURL(file);
          }} />
      </div>
    )}

    {ocrImage && (
      <img src={ocrImage} style={{ width: '100%', borderRadius: 10, border: '1px solid ' + C.border, maxHeight: 160, objectFit: 'contain', background: '#000' }} />
    )}

    {ocrLoading && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(30,41,59,0.8)', borderRadius: 10, border: '1px solid ' + C.border }}>
        <div style={{ width: 20, height: 20, border: '2px solid rgba(99,102,241,0.3)', borderTopColor: C.blue, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>Reading label text...</span>
      </div>
    )}

    {ocrText && !ocrLoading && (
      <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 12px' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Raw Text Read</div>
        <div style={{ fontSize: 11, color: '#cbd5e1', fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 80, overflowY: 'auto' }}>{ocrText}</div>
      </div>
    )}

    {ocrParsed && !ocrLoading && (
      <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 12, padding: '12px' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
          ✨ Extracted — Edit if needed
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <div style={{ fontSize: 9, color: C.textMuted, fontWeight: 700, marginBottom: 3 }}>ITEM NAME</div>
            <input value={ocrParsed.name} onChange={e => setOcrParsed(p => p ? { ...p, name: e.target.value } : p)}
              style={{ width: '100%', padding: '7px 10px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: '#e2e8f0', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 9, color: C.textMuted, fontWeight: 700, marginBottom: 3 }}>PRICE (₹)</div>
              <input value={ocrParsed.price} onChange={e => setOcrParsed(p => p ? { ...p, price: e.target.value } : p)}
                type="number" placeholder="0.00"
                style={{ width: '100%', padding: '7px 10px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: '#67e8f9', outline: 'none', boxSizing: 'border-box', fontWeight: 700 }} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: C.textMuted, fontWeight: 700, marginBottom: 3 }}>QTY</div>
              <input value={ocrParsed.qty} onChange={e => setOcrParsed(p => p ? { ...p, qty: e.target.value } : p)}
                type="number" min="1"
                style={{ width: '100%', padding: '7px 10px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: '#e2e8f0', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            if (!ocrParsed.name.trim()) return;
            onAddItem(ocrParsed.name.trim(), parseInt(ocrParsed.qty) || 1, parseFloat(ocrParsed.price) || 0);
            setSessionItems(prev => {
              const existing = prev.findIndex(i => i.description === ocrParsed.name.trim());
              if (existing >= 0) { const u = [...prev]; u[existing].qty += parseInt(ocrParsed.qty) || 1; return u; }
              return [...prev, { barcode: 'OCR', description: ocrParsed.name.trim(), qty: parseInt(ocrParsed.qty) || 1, rate: parseFloat(ocrParsed.price) || 0 }];
            });
            setLastAdded(ocrParsed.name.trim());
            setTimeout(() => setLastAdded(null), 2000);
            setOcrImage(null); setOcrText(''); setOcrParsed(null);
          }}
          style={{
            width: '100%', marginTop: 10, padding: '10px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg,#3b82f6,#9333ea)', color: '#fff',
            fontWeight: 800, fontSize: 13, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
          }}>
          {context === 'catalog' ? '➕ Add to Catalog' : '➕ Add to Cart'}
        </button>
      </div>
    )}

    {!ocrImage && !ocrLoading && (
      <div style={{ textAlign: 'center', color: C.textMuted, fontSize: 11, padding: '12px 0', lineHeight: 1.7 }}>
        📦 Point camera at a price sticker, product label,<br />or supplier slip to read text automatically.
      </div>
    )}
  </div>
)}


            {/* ── Last added flash (all tabs) ── */}
            {lastAdded && (
              <div style={{
                margin: '0 16px',
                background: 'rgba(16,185,129,0.12)',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 10, padding: '8px 12px',
                display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
              }}>
                <span style={{ fontSize: 16 }}>✅</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#34d399' }}>
                  Added: {lastAdded}
                </span>
              </div>
            )}
            {loading && !lastAdded && (
              <div style={{ padding: '6px 16px', fontSize: 11, color: C.textMuted }}>
                Looking up product...
              </div>
            )}

            {/* ── Scanned items list ── */}
            <div style={{ flex: 1, padding: '10px 16px', overflowY: 'auto' }}>
              {sessionItems.length === 0 ? (
                <div style={{
                  textAlign: 'center', color: C.textMuted, fontSize: 12,
                  padding: '24px 0', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 8,
                }}>
                  <div style={{ fontSize: 32, opacity: 0.4 }}>🔍</div>
                  <div style={{ fontWeight: 600 }}>{context === 'catalog' ? 'Scanned products appear here' : 'Scanned items appear here'}</div>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>Scan a barcode to get started</div>
                </div>
              ) : (
                <>
                  <div style={{
                    fontSize: 9, fontWeight: 700, color: C.textMuted,
                    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8,
                  }}>
                    Session — {totalItems} {context === 'catalog' ? 'product' : 'item'}{totalItems !== 1 ? 's' : ''}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {sessionItems.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: 'rgba(30,41,59,0.7)',
                        border: '1px solid ' + C.border,
                        borderRadius: 10,
                        animation: idx === 0 ? 'slideIn 0.2s ease' : 'none',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 12, fontWeight: 600, color: '#e2e8f0',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {item.description}
                          </div>
                          <div style={{ fontSize: 9, color: C.textMuted, fontFamily: 'monospace', marginTop: 1 }}>
                            {item.barcode}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: '#a5b4fc',
                            background: 'rgba(99,102,241,0.15)',
                            borderRadius: 6, padding: '2px 7px',
                          }}>×{item.qty}</span>
                          {item.rate > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#67e8f9' }}>
                              ₹{(item.qty * item.rate).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
